import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { registerAdminServiceWorker } from "@/lib/register-admin-sw";
import { ADMIN_APP_LINKS, ADMIN_APP_META } from "@/lib/admin-app-head";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Staff Login — Plunkets" },
      { name: "description", content: "Secure staff sign-in for Plunkets Pub Dublin." },
      { property: "og:title", content: "Staff Login — Plunkets Pub Dublin" },
      { property: "og:description", content: "Secure staff sign-in for Plunkets Pub Dublin." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://plunkets.ie/auth" },
      { property: "og:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "robots", content: "noindex" },
      ...ADMIN_APP_META,
    ],
    links: ADMIN_APP_LINKS,
  }),
  component: AuthPage,
});

function AuthPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    registerAdminServiceWorker();
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) nav({ to: "/admin" });
    });
  }, [nav]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      nav({ to: "/admin" });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-12">
        <div className="flex flex-col items-center text-center">
          <img
            src="/__l5e/assets-v1/8e4e1149-6c7d-4be6-94ba-348c7e90e75c/plunkets-horizontal.png"
            alt="Plunkets Pub logo"
            className="h-16 w-auto"
          />
          <p className="mt-4 text-xs uppercase tracking-[0.4em] text-primary">Staff Console</p>
          <h1 className="mt-2 font-display text-2xl text-foreground">Staff Login</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to the admin console.
          </p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl border border-border bg-card/60 p-6">
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy}>
            Sign in
          </Button>
        </form>
        <p className="mt-4 text-xs text-muted-foreground">
          Staff accounts are invite-only. Contact an owner to be added to the team.
        </p>
      </div>
    </div>
  );
}
